<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="basicInfoForm">
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>Hosting Institution Name</label>
          <p class="label-info">
            Full Name of the Hosting Institution offering Services and acting as main contact point
            for the Services.
          </p>
          <v-text-field
            v-model.trim="hosting.name"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('website') }}</label>
          <p class="label-info">Website with information about the Hosting Institution.</p>
          <v-text-field
            v-model="hosting.url"
            outlined
            :placeholder="$t('website')"
            :rules="requiredRule() && checkURL()"
            class="required"
            prepend-inner-icon="mdi-link-variant"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" xl="6" lg="6" md="6" sm="12">
                    <label>Abbreviation</label>
                    <p class="label-info">
                      Abbreviation of the Hosting Institution.
                    </p>
                    <v-text-field
                      v-model.trim="optionalHosting.abbreviation"
                      outlined
                      :placeholder="'Abbreviation'"
                      clearable
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updateHostingBasicInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-else
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>

    <v-dialog v-model="warningDialog" max-width="800px" persistent>
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-text class="text-center">
          <h4>
            The Hosting Institution name is different from the one provided during registration.
          </h4>
          <h4 class="mt-5">
            Would you like to continue with the new name?
          </h4>
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col class="text-right">
              <v-btn
                @click="keepOriginalName"
                depressed
                rounded
                medium
                class="neutral800--text font-weight-bold"
                color="primary200"
                outlined
              >
                Keep original name
              </v-btn>
            </v-col>
            <v-col class="text-left">
              <v-btn
                @click="useNewName"
                depressed
                rounded
                medium
                color="primary200"
                class="white--text font-weight-bold"
              >
                Use new name
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'AddHostingBasicInfo',
  components: { AlertUnsavedChanges },
  mixins: [util],
  data() {
    return {
      hosting: {
        name: '',
        url: '',
      },
      optionalHosting: {
        abbreviation: '',
      },
      unsavedChanges: false,
      initialHosting: {},
      initialOPtionalHosting: {},
      dataLoaded: false,
      institutionFromToken: '',
      firstTime: null,
      warningDialog: false,
    };
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    hosting: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.hosting.name !== this.initialHosting.name ||
          this.hosting.url !== this.initialHosting.url;
      },
    },
    optionalHosting: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalHosting.abbreviation !== this.initialOPtionalHosting.abbreviation;
      },
    },
  },
  mounted() {
    if (this.edit) {
      this.getHosting();
    }
  },
  created() {
    this.firstTime = sessionStorage.getItem('firstTime', this.firstTime);
    if (this.firstTime === 'true') {
      const token = localStorage.getItem('token');

      if (token) {
        this.institutionFromToken = JSON.parse(atob(token.split('.')[1]));
        this.hosting.name = this.institutionFromToken.institution;

        console.log('Institution from token:', this.institutionFromToken.institution);
      }
    }
  },
  methods: {
    useNewName() {
      const isFormValid = this.$refs.basicInfoForm.validate();

      if (isFormValid) {
        this.$emit('tab', 1);
        this.$emit('hostingProviderInfo', { ...this.hosting });
      }
      this.warningDialog = false;
    },
    keepOriginalName() {
      this.warningDialog = false;
      this.hosting.name = this.institutionFromToken.institution;
    },
    next() {
      const isFormValid = this.$refs.basicInfoForm.validate();

      if (
        this.firstTime === 'true' &&
        this.institutionFromToken.institution !== this.hosting.name
      ) {
        EventBus.$emit('snackbar', {
          text: 'Make sure the Hosting Institution name matches the one used during registration.',
          color: 'primary200',
          timeout: 2000,
        });
        this.warningDialog = true;
        return;
      }

      if (isFormValid) {
        this.$emit('tab', 1);
        this.$emit('hostingProviderInfo', { ...this.hosting });
      }
    },
    getHosting() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/hosting-institutions/' + id)
        .then(res => {
          console.log('Get Hosting Institution Basic Info', res.data);

          this.hosting.name = res.data.name;
          this.hosting.url = res.data.url;
          this.optionalHosting.abbreviation = res.data.abbreviation;

          this.initialHosting = {
            name: res.data.name,
            url: res.data.url,
          };
          this.initialOPtionalHosting = {
            abbreviation: res.data.abbreviation,
          };

          this.dataLoaded = true;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.getHosting();
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    updateHostingBasicInfo() {
      const id = this.$route.params.id;

      const hostingUpdateData = {
        id: id,
        name: this.hosting.name,
        url: this.hosting.url,
        abbreviation: this.optionalHosting.abbreviation,
      };

      this.deleteEmptyProperties(hostingUpdateData);

      const isFormValid = this.$refs.basicInfoForm.validate();
      if (isFormValid) {
        this.axios
          .post('/api/v1/hosting-institutions/basic-info', hostingUpdateData)
          .then(response => {
            console.log('Hosting Institution info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Hosting Institution Updated Successfully',
              color: 'primary200',
            });
            this.initialHosting = { ...this.hosting };
            this.initialOPtionalHosting = { ...this.optionalHosting };
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating Hosting Institution info:', error);
          });
      }
    },
  },
};
</script>
